import { registerPlugin } from '@capacitor/core'

export type FlightMapMode = 'globe' | 'flat'

export interface FlightMapFrame {
  x: number
  y: number
  width: number
  height: number
}

export interface FlightMapPlugin {
  show(opts: {
    frame: FlightMapFrame
    departure?: { lat: number; lon: number; icao: string }
    arrival?: { lat: number; lon: number; icao: string }
    departureDateTime?: string
    arrivalDateTime?: string
    /**
     * Single-point marker drawn when no route is provided (used on Home when the
     * crew has no upcoming flight). Centers the camera on this coordinate.
     * The optional `label` populates the pill below the dot.
     */
    location?: { lat: number; lon: number; label?: string }
    mode: FlightMapMode
    /**
     * When false, the webview captures every touch and the native map is purely visual.
     * Default true — the map intercepts touches outside the React-rendered controls.
     */
    interactive?: boolean
    /**
     * Camera altitude in meters for globe mode. Defaults to ~18,000 km (full-earth view).
     * When `edgePadding` is also provided, this acts as a MINIMUM — MapKit picks the
     * center from the padded fit, then altitude is bumped up to honor this override.
     */
    cameraAltitude?: number
    /**
     * Inset padding (points) when framing the route's bounding rect. When set,
     * the plugin uses `setVisibleMapRect(_:edgePadding:)` to fit the route within
     * the padded area — bottom-heavy values push the route into the upper half.
     * Applied in both globe and flat modes when present.
     */
    edgePadding?: { top: number; left: number; bottom: number; right: number }
    /**
     * When true (default), globe mode uses `MKImageryMapConfiguration` with
     * realistic elevation — 3D terrain plus atmospheric scattering at the limb.
     * Pass false for a clean satellite globe with no atmosphere/haze.
     */
    realisticElevation?: boolean
  }): Promise<void>
  setFrame(opts: FlightMapFrame): Promise<void>
  /**
   * Switch the map between globe (3D satellite imagery) and flat (standard 2D)
   * modes after `show()`. The toggle UI lives in React; this is the bridge used
   * by it to actually flip the native map configuration.
   */
  setMode(opts: { mode: FlightMapMode }): Promise<void>
  /**
   * Register an extra interactive rect (in webview coordinates) so the passthrough
   * webview captures touches inside it instead of forwarding them to the native map.
   * Used by overlays such as the resizable bottom drawers shown over the map.
   */
  setOverlayRect(opts: FlightMapFrame): Promise<void>
  clearOverlayRect(): Promise<void>
  /**
   * Like `setOverlayRect`, but reserved for in-map controls (e.g. the React-rendered
   * mode-toggle pill). Lives in a separate slot so it doesn't fight with the
   * drawer overlay for the same rect.
   */
  setControlRect(opts: FlightMapFrame): Promise<void>
  clearControlRect(): Promise<void>
  hide(): Promise<void>
  /**
   * Reverse-geocode a coordinate using Apple's CLGeocoder (no API key, no rate
   * limit, results localised to the device language). Both fields can be `null`
   * when Apple has no placemark for the coordinate (e.g. mid-ocean).
   */
  reverseGeocode(opts: { lat: number; lon: number }): Promise<{
    cityName: string | null
    timezone: string | null
  }>
}

export const FlightMapNative = registerPlugin<FlightMapPlugin>('FlightMap')
